import { useDeferredValue, useEffect, useState } from 'react'
import { Search } from 'lucide-react'

import { useLoadMore } from '@/shared/lib/use-load-more'
import { Button } from '@/ui/button'
import { DialogFooter } from '@/ui/dialog'
import { Input } from '@/ui/input'
import { LoadingNotice } from '@/ui/loading-notice'
import { RetryNotice } from '@/ui/retry-notice'
import { RolePicker } from '@/ui/role-picker'

import { useAssignableRoles } from '@/modules/iam/features/roles/role-queries'
import type { UserCandidate } from '@/modules/iam/api/users-api'
import {
  assignUserRoleErrorMessage,
  useAssignUserRole,
  useUserAccessProfile,
  useUserCandidates,
} from './user-queries'

export function AssignUserRolePanel({
  entityId,
  onClose,
  onPendingChange,
  onAssigned,
}: {
  entityId: string
  onClose: () => void
  onPendingChange?: (pending: boolean) => void
  onAssigned?: (message: string) => void
}) {
  const [inputQuery, setInputQuery] = useState('')
  const query = useDeferredValue(inputQuery)
  const [selectedUser, setSelectedUser] = useState<UserCandidate | null>(null)
  const [roleId, setRoleId] = useState<string | null>(null)
  const candidatesQuery = useUserCandidates(entityId, { query })
  const rolesQuery = useAssignableRoles(entityId)
  const profileQuery = useUserAccessProfile(entityId, selectedUser?.userId ?? null)
  const assign = useAssignUserRole()
  const pager = useLoadMore(candidatesQuery, 'users')

  useEffect(() => {
    onPendingChange?.(assign.isPending)
  }, [assign.isPending, onPendingChange])

  const heldIds = new Set(
    (profileQuery.data?.grants ?? [])
      .filter((grant) => grant.entityId === entityId && grant.viaGroupId === null)
      .map((grant) => grant.roleId),
  )
  const roles = (rolesQuery.data ?? []).filter((role) => !heldIds.has(role.roleId))

  function submit() {
    const role = roles.find((item) => item.roleId === roleId)
    if (!selectedUser || !role) return
    assign.mutate(
      { entityId, userId: selectedUser.userId, roleId: role.roleId },
      {
        onSuccess: () => {
          onAssigned?.(`Assigned ${role.name} to ${selectedUser.name}`)
          onClose()
        },
      },
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2" />
        <Input value={inputQuery} onChange={(event) => setInputQuery(event.target.value)} placeholder="Search users by name or email…" aria-label="Search users" disabled={assign.isPending} className="pl-7" />
      </div>

      {candidatesQuery.isLoading ? (
        <LoadingNotice message="Loading users…" />
      ) : candidatesQuery.error ? (
        <RetryNotice message={candidatesQuery.error.message} onRetry={() => void candidatesQuery.refetch()} />
      ) : (
        <ul aria-label="Users" className="border-border max-h-56 overflow-y-auto rounded-md border">
          {pager.items.length === 0 ? (
            <li className="text-muted-foreground px-3 py-4 text-center text-sm">No users match your search.</li>
          ) : pager.items.map((candidate) => (
            <li key={candidate.userId}>
              <button
                type="button"
                aria-pressed={candidate.userId === selectedUser?.userId}
                disabled={assign.isPending}
                onClick={() => { setSelectedUser(candidate); setRoleId(null); assign.reset() }}
                className="hover:bg-foreground/[0.04] aria-pressed:bg-foreground/[0.06] flex w-full flex-col items-start px-3 py-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
              >
                <span className="text-sm font-medium">{candidate.name}</span>
                <span className="text-muted-foreground text-[11px]">{candidate.email}</span>
              </button>
            </li>
          ))}
          {pager.hasNextPage ? <li><button {...pager.buttonProps} className="text-muted-foreground w-full px-3 py-2 text-sm">{pager.label}</button></li> : null}
        </ul>
      )}

      {selectedUser ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm">Assigning a role to <span className="font-medium">{selectedUser.name}</span></p>
          <RolePicker aria-label="Role" roles={roles} isLoading={rolesQuery.isLoading || profileQuery.isLoading} error={rolesQuery.error} onRetry={() => void rolesQuery.refetch()} value={roleId} onChange={setRoleId} disabled={assign.isPending} />
        </div>
      ) : null}

      {assign.isError ? <p role="alert" className="text-destructive">{assignUserRoleErrorMessage(assign.error)}</p> : null}

      <DialogFooter>
        <Button type="button" variant="outline" size="sm" disabled={assign.isPending} onClick={onClose}>Cancel</Button>
        <Button type="button" size="sm" disabled={assign.isPending || !selectedUser || !roleId} onClick={submit}>{assign.isPending ? 'Assigning…' : 'Assign role'}</Button>
      </DialogFooter>
    </div>
  )
}
